import { z } from 'zod/v4';

import {
    AskUserMessageSchema,
    ChartSavedMessageSchema,
    ChartsMessageSchema,
    DatasetMessageSchema,
    TodoMessageSchema,
    VegaLiteMessageSchema,
} from '../agent/types';

/** Workflow skills a starter button can request explicitly; user-visible text is never matched. */
export const InsightsAgentSkillSchema = z.enum(['find-savings', 'find-at-risk-components']);
export type InsightsAgentSkill = z.infer<typeof InsightsAgentSkillSchema>;

export const InsightsAgentMessageSchema = z.object({
    type: z.literal('text'),
    text: z.string().trim().min(1).max(20_000),
    skill: InsightsAgentSkillSchema.optional(),
});
export type InsightsAgentMessage = z.infer<typeof InsightsAgentMessageSchema>;

export const InsightsAgentClarificationResponseSchema = z.object({
    runId: z.string().min(1).max(200),
    toolCallId: z.string().min(1).max(500),
    messageId: z.string().min(1).max(500),
    responses: z.array(z.string().trim().min(1).max(4_000)).min(1).max(20),
});
export type InsightsAgentClarificationResponse = z.infer<typeof InsightsAgentClarificationResponseSchema>;

export const InsightsAgentToolPayloadSchema = z.discriminatedUnion('type', [
    AskUserMessageSchema,
    TodoMessageSchema,
    DatasetMessageSchema,
    VegaLiteMessageSchema,
    ChartSavedMessageSchema,
    ChartsMessageSchema,
]);
export type InsightsAgentToolPayload = z.infer<typeof InsightsAgentToolPayloadSchema>;

/** Provider-neutral token accounting. Fields are absent when the model provider does not report them. */
export const InsightsAgentTokenUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    totalTokens: z.number().int().nonnegative().optional(),
    reasoningTokens: z.number().int().nonnegative().optional(),
    cachedInputTokens: z.number().int().nonnegative().optional(),
});
export type InsightsAgentTokenUsage = z.infer<typeof InsightsAgentTokenUsageSchema>;

export const InsightsAgentToolInvocationStateSchema = z.enum([
    'partial-call',
    'call',
    'result',
    'approval-requested',
    'approval-responded',
    'output-error',
    'output-denied',
]);

/** Product-safe projection of persisted Mastra parts. Provider and reasoning fields are never exposed. */
export const InsightsAgentMessagePartSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string() }),
    z.object({
        type: z.literal('tool-invocation'),
        toolInvocation: z.object({
            toolCallId: z.string(),
            toolName: z.string(),
            state: InsightsAgentToolInvocationStateSchema.optional(),
            isError: z.boolean().optional(),
            errorMessage: z.string().max(4_000).optional(),
            uiData: InsightsAgentToolPayloadSchema.optional(),
        }),
    }),
    z.object({
        type: z.literal('data-tool-call-suspended'),
        data: z.object({
            runId: z.string(),
            toolCallId: z.string(),
            toolName: z.string().optional(),
            suspendPayload: AskUserMessageSchema,
            resumed: z.boolean().optional(),
        }),
    }),
]);
export type InsightsAgentMessagePart = z.infer<typeof InsightsAgentMessagePartSchema>;

export const InsightsAgentConversationMessageMetadataSchema = z.object({
    visibility: z.literal('hidden').optional(),
    resumedToolCallId: z.string().optional(),
    responses: z.array(z.string()).optional(),
});
export type InsightsAgentConversationMessageMetadata = z.infer<typeof InsightsAgentConversationMessageMetadataSchema>;

export const InsightsAgentConversationMessageSchema = z.object({
    id: z.string(),
    role: z.enum(['user', 'assistant']),
    parts: z.array(InsightsAgentMessagePartSchema),
    metadata: InsightsAgentConversationMessageMetadataSchema.optional(),
    createdAt: z.string(),
});
export type InsightsAgentConversationMessage = z.infer<typeof InsightsAgentConversationMessageSchema>;

export const InsightsAgentConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    summary: z.string().optional(),
    purpose: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    forkedFromConversationId: z.string().optional(),
});
export type InsightsAgentConversation = z.infer<typeof InsightsAgentConversationSchema>;

export const InsightsAgentConversationDetailSchema = z.object({
    conversation: InsightsAgentConversationSchema,
    messages: z.array(InsightsAgentConversationMessageSchema),
});
export type InsightsAgentConversationDetail = z.infer<typeof InsightsAgentConversationDetailSchema>;

/** Direct stream event. IDs are Mastra's canonical run/tool-call IDs; no cursor or sequence is added. */
export const InsightsAgentEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('run.started'), runId: z.string() }),
    z.object({ type: z.literal('text.delta'), runId: z.string(), delta: z.string() }),
    z.object({
        type: z.literal('tool.started'),
        runId: z.string(),
        toolCallId: z.string(),
        toolName: z.string(),
    }),
    z.object({
        type: z.literal('tool.completed'),
        runId: z.string(),
        toolCallId: z.string(),
        toolName: z.string(),
        isError: z.boolean(),
        errorMessage: z.string().max(4_000).optional(),
        uiData: InsightsAgentToolPayloadSchema.optional(),
    }),
    z.object({
        type: z.literal('run.suspended'),
        runId: z.string(),
        toolCallId: z.string(),
        toolName: z.string().optional(),
        payload: AskUserMessageSchema,
    }),
    z.object({ type: z.literal('run.completed'), runId: z.string(), usage: InsightsAgentTokenUsageSchema.optional() }),
    z.object({ type: z.literal('run.cancelled'), runId: z.string(), message: z.string() }),
    z.object({ type: z.literal('run.failed'), runId: z.string(), message: z.string() }),
]);
export type InsightsAgentEvent = z.infer<typeof InsightsAgentEventSchema>;

export const InsightsAgentChartPointExplanationEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('conversation.created'), conversation: InsightsAgentConversationSchema }),
    z.object({ type: z.literal('stream.event'), event: InsightsAgentEventSchema }),
]);
export type InsightsAgentChartPointExplanationEvent = z.infer<typeof InsightsAgentChartPointExplanationEventSchema>;

/** A conversation chart persisted as a monitoring chart by the direct save boundary. */
export const InsightsAgentSavedChartSchema = z.object({
    chartId: z.string().uuid(),
    title: z.string(),
    url: z.string(),
});
export type InsightsAgentSavedChart = z.infer<typeof InsightsAgentSavedChartSchema>;
